// ================================================================
// src/freeStudy/hooks.js
// پل React ↔ منطق سیستم
// ================================================================
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getPurchaseState, getProgressState, mutateProgressState } from './storage.js';
import {
  purchaseVideo as doPurchaseVideo,
  purchaseBundle as doPurchaseBundle,
} from './purchase.js';
import {
  computeBundlePrice,
  computeVideoPrice,
  getOwnedCount,
  getRemainingCount,
  getUnownedVideos,
  isEffectivelyFree,
  isUnlocked,
} from './core.js';

// ================================================================
// A) useCoursePurchase — state خرید
// ================================================================
export function useCoursePurchase(userId, course) {
  const courseId = course?.id;
  const [state, setState] = useState({
    unlockedVideoIds: [],
    totalPaid: 0,
    totalRefunded: 0,
    transactions: [],
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const idemRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!userId || !courseId) return;
    const next = await getPurchaseState(userId, courseId);
    setState(next);
  }, [userId, courseId]);

  // بارگذاری + refetch روی focus/visibility
  useEffect(() => {
    refresh().catch(() => {});

    // ⚠️ storage event دیگر معنی ندارد چون داده در سرور است.
    //    فقط روی focus/visibility دوباره از سرور بخوان.
    const onFocus = () => { refresh().catch(() => {}); };
    const onVis = () => {
      if (!document.hidden) refresh().catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refresh]);

  const buyVideo = useCallback(
    async (videoId) => {
      if (!userId || !course || isProcessing) return { ok: false };
      setIsProcessing(true);
      // idempotency key در طول همین فرایند ثابت می‌ماند
      const key = idemRef.current || `idem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      idemRef.current = key;
      try {
        const res = await doPurchaseVideo({
          userId,
          course,
          videoId,
          idempotencyKey: key,
        });
        refresh();
        return res;
      } finally {
        idemRef.current = null;
        setIsProcessing(false);
      }
    },
    [userId, course, isProcessing, refresh]
  );

  const buyBundle = useCallback(async () => {
    if (!userId || !course || isProcessing) return { ok: false };
    setIsProcessing(true);
    const key = idemRef.current || `idem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    idemRef.current = key;
    try {
      const res = await doPurchaseBundle({
        userId,
        course,
        idempotencyKey: key,
      });
      refresh();
      return res;
    } finally {
      idemRef.current = null;
      setIsProcessing(false);
    }
  }, [userId, course, isProcessing, refresh]);

  const unlockedSet = useMemo(
    () => new Set(state.unlockedVideoIds || []),
    [state.unlockedVideoIds]
  );

  return {
    state,
    unlockedSet,
    isProcessing,
    buyVideo,
    buyBundle,
    refresh,
  };
}

// ================================================================
// B) useCourseProgress — progress با videoId
// ================================================================
export function useCourseProgress(userId, course) {
  const courseId = course?.id;
  const [state, setState] = useState({
    completedVideoIds: [],
    notes: {},
    lastVideoId: null,
    lastVisited: null,
  });
  const [tick, setTick] = useState(0);

  const read = useCallback(async () => {
    if (!userId || !courseId) return;
    const next = await getProgressState(userId, courseId, course);
    setState(next);
  }, [userId, courseId, course]);

  useEffect(() => {
    read().catch(() => {});
  }, [read, tick]);

  useEffect(() => {
    // ⚠️ storage event دیگر معنی ندارد چون داده در سرور است.
    //    فقط روی focus/visibility دوباره از سرور بخوان.
    const onFocus = () => { read().catch(() => {}); };
    const onVis = () => {
      if (!document.hidden) read().catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [read]);

  const markVisited = useCallback(async () => {
    if (!userId || !courseId) return;
    await mutateProgressState(userId, courseId, course, (cur) => ({
      ...cur,
      lastVisited: Date.now(),
    }));
    setTick((t) => t + 1);
  }, [userId, courseId, course]);

  const markVideoWatched = useCallback(
    async (videoId) => {
      if (!userId || !courseId || !videoId) return;
      await mutateProgressState(userId, courseId, course, (cur) => {
        const set = new Set(cur.completedVideoIds || []);
        set.add(videoId);
        return {
          ...cur,
          completedVideoIds: Array.from(set),
          lastVideoId: videoId,
          lastVisited: Date.now(),
        };
      });
      setTick((t) => t + 1);
    },
    [userId, courseId, course]
  );

  const unmarkVideo = useCallback(
    async (videoId) => {
      if (!userId || !courseId || !videoId) return;
      await mutateProgressState(userId, courseId, course, (cur) => ({
        ...cur,
        completedVideoIds: (cur.completedVideoIds || []).filter(
          (id) => id !== videoId
        ),
      }));
      setTick((t) => t + 1);
    },
    [userId, courseId, course]
  );

  const saveNote = useCallback(
    async (videoId, text) => {
      if (!userId || !courseId || !videoId) return;
      await mutateProgressState(userId, courseId, course, (cur) => ({
        ...cur,
        notes: { ...(cur.notes || {}), [videoId]: text },
      }));
      setTick((t) => t + 1);
    },
    [userId, courseId, course]
  );

  const completedSet = useMemo(
    () => new Set(state.completedVideoIds || []),
    [state.completedVideoIds]
  );

  return {
    state,
    completedSet,
    markVisited,
    markVideoWatched,
    unmarkVideo,
    saveNote,
  };
}

// ================================================================
// C) usePricing — قیمت‌های computed
// ================================================================
export function usePricing(course, purchaseState) {
  return useMemo(() => {
    if (!course) {
      return {
        fullCoursePrice: 0,
        bundlePrice: 0,
        ownedCount: 0,
        remainingCount: 0,
        unownedVideos: [],
        isComplete: true,
        totalVideos: 0,
      };
    }

    const unlockedIds = purchaseState?.unlockedVideoIds || [];
    const unowned = getUnownedVideos(course, unlockedIds);
    const bundlePrice = computeBundlePrice(course, purchaseState);
    const ownedCount = getOwnedCount(course, unlockedIds);
    const remainingCount = getRemainingCount(course, unlockedIds);

    return {
      fullCoursePrice: Number(course?.pricing?.fullCoursePrice) || 0,
      bundlePrice,
      ownedCount,
      remainingCount,
      unownedVideos: unowned,
      isComplete: unowned.length === 0,
      totalVideos: course.videos.length,
      totalPaid: purchaseState?.totalPaid || 0,
    };
  }, [course, purchaseState]);
}

// ================================================================
// D) useVideoStatus — وضعیت هر ویدیو (locked/unlocked/current/watched)
// ================================================================
export function useVideoStatus(unlockedSet, completedSet, lastVideoId) {
  return useCallback(
    (video) => {
      if (completedSet?.has(video.id)) return 'watched';
      if (!unlockedSet?.has(video.id) && !isEffectivelyFree(video)) return 'locked';
      if (lastVideoId === video.id) return 'current';
      return 'unlocked';
    },
    [unlockedSet, completedSet, lastVideoId]
  );
}

export { isUnlocked, isEffectivelyFree, computeVideoPrice };